import { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What documents are required for property registration?",
      answer: "Key documents include: Sale Deed, Original Title Deed, Encumbrance Certificate, Property Tax Receipt, Identity Proof (Aadhar/PAN), Address Proof, Passport-sized photographs, and NOC from the society/builder (if applicable). Our team will provide a complete checklist based on your specific property type."
    },
    {
      question: "How long does the property registration process take?",
      answer: "Typically, the registration process takes 7-15 business days depending on document readiness and sub-registrar office schedules. We expedite the process through efficient coordination and ensure minimal delays."
    },
    {
      question: "What are the stamp duty charges in Karnataka?",
      answer: "In Karnataka, stamp duty is typically 5% for men, 3% for women, and 3% for joint registration (man and woman). Registration charges are additional at 1%. However, rates may vary based on location and property type. We provide accurate calculations for your specific case."
    },
    {
      question: "Can you help with property document verification?",
      answer: "Yes, absolutely! We provide comprehensive document verification services including title verification, encumbrance certificate checks, and legal due diligence to ensure the property is free from legal disputes and liabilities."
    },
    {
      question: "Do you assist with sub-registrar office coordination?",
      answer: "Yes, we handle complete coordination with the sub-registrar office including appointment scheduling, document submission, and ensuring all parties are present during registration. We make the entire process hassle-free for you."
    },
    {
      question: "What is an Encumbrance Certificate and why is it important?",
      answer: "An Encumbrance Certificate (EC) is a legal document that shows all transactions related to a property for a specific period. It's crucial as it verifies that the property is free from any legal or monetary liabilities. We assist in obtaining and verifying ECs."
    },
    {
      question: "Can you help with property sale deed drafting?",
      answer: "Yes, our experienced legal team drafts comprehensive sale deeds that are legally sound and compliant with all regulations. We ensure all terms and conditions are clearly documented to protect your interests."
    },
    {
      question: "What is your consultation process and charges?",
      answer: "We offer free initial consultation where we understand your requirements and provide guidance. Our service charges are transparent and competitive, with no hidden costs. Contact us for detailed quotation based on your specific needs."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left content */}
          <div className="lg:sticky lg:top-32">
            <div className="inline-block bg-blue-50 rounded-full px-6 py-2 mb-6">
              <span className="text-blue-700 font-semibold text-sm tracking-wide">FAQ</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Frequently Asked Questions
            </h2>
            
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Find answers to common questions about property registration, legal documentation, and our services. Can't find what you're looking for?
            </p>

            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-8 border border-emerald-200">
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Still have questions?
              </h3>
              <p className="text-slate-600 mb-6">
                Our expert team is here to help you with all your property registration queries.
              </p>
              <div className="space-y-3">
                <a 
                  href="tel:+918217026707"
                  className="block text-center bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
                >
                  Call: +91 82170 26707
                </a>
                <a 
                  href="https://wa.me/918217026707"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center bg-white text-emerald-700 px-6 py-3 rounded-lg font-semibold hover:bg-emerald-50 transition-colors border border-emerald-200"
                >
                  WhatsApp Us
                </a>
              </div>
            </div>
          </div>

          {/* Right - FAQ accordion */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden hover:border-emerald-300 transition-colors"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex justify-between items-center gap-4 text-left hover:bg-slate-100 transition-colors"
                >
                  <span className="font-bold text-slate-900 pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`text-emerald-600 flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
